import { useEffect, useState } from 'react';
import { OrdersService } from '@/service';
import { useToast } from '@/context/ToastContext';
import { buildPlayerModelOptions, PlayerModelOption } from '@/library/PlayerModelsHelper';
import SearchableMultiSelectDropdown from '@/common/SearchableMultiSelectDropdown/SearchableMultiSelectDropdown';
import type { PlayerModelClubType } from '@/api/models/PlayerModels';

const clubTypeOrder: PlayerModelClubType[] = ['driver', 'fairway', 'hybrid', 'iron', 'utility', 'wedge'];

interface Props {
  playerId?: number;
  enabled: boolean;
  visible: boolean;
  value: PlayerModelOption[];
  // eslint-disable-next-line no-unused-vars
  onChange: (value: PlayerModelOption[]) => void;
  className?: string;
}

export default function PlayerModelsFilter({ playerId, enabled, visible, value, onChange, className }: Props) {
  const { showToast } = useToast();
  const [loaded, setLoaded] = useState<{ playerId: number; options: PlayerModelOption[] }>();

  useEffect(() => {
    let isCurrentRequest = true;
    setLoaded(undefined);
    if (!enabled || playerId === undefined) return;

    const loadModels = async () => {
      try {
        const groups = await OrdersService.getPlayerModels(playerId);
        // Filter Putter from the Club Landing page, Components doesn't have player search
        // TODO: When developing the Putter we'll need to do the opposite and check by current page type
        const sortedGroups = groups.filter((group) => group.clubType !== 'putter').sort((a, b) => clubTypeOrder.indexOf(a.clubType) - clubTypeOrder.indexOf(b.clubType));
        if (isCurrentRequest) setLoaded({ playerId, options: buildPlayerModelOptions(sortedGroups) });
      } catch {
        if (isCurrentRequest) showToast({ message: 'Unable to load player models.', status: 'error' });
      }
    };
    void loadModels();
    return () => {
      isCurrentRequest = false;
    };
  }, [enabled, playerId, showToast]);

  if (!enabled || !visible || !loaded || loaded.playerId !== playerId || loaded.options.length === 0) return null;

  return (
    <SearchableMultiSelectDropdown
      label="Player Models"
      options={loaded.options}
      value={value}
      onChange={(selected) => onChange(selected.map((selection) => loaded.options.find((option) => option.value === selection.value)!))}
      className={className}
    />
  );
}
